import React, { ReactNode, useCallback, useState } from "react";
import { Box, TypographyProps } from "@mui/material";
import {
  AccountSelectGroup,
  AccountsApiListAndSearchAllAccountsRequest,
  AccountsApiSelectGroupsIdRequest,
} from "@bn/api/apiv1";
import { useCreateSelectedGroups } from "@api/v1/hooks/useSelectedGroups";
import { DepotsSelectorModal } from "@features/selectDepots/components/DepotsSelectorModal";
import { usePublicOrPrivateAccounts } from "@features/sharedPortfolio/hooks/usePublicOrPrivateAccounts";
import { filterOutHiddenAccounts } from "../../hooks/useSelectGroupsForId";
import { DepotsSelectorButton } from "./components/DepotsSelectorButton";
import { DepotsSelectorBody } from "./components/DepotsSelectorBody";

export type Props = {
  selectedGroup?: AccountSelectGroup;
  accountParams?: AccountsApiListAndSearchAllAccountsRequest;
  selectedGroupParams: AccountsApiSelectGroupsIdRequest;
  color?: string;
  showOptions?: boolean;
  onShowClick?: (val: boolean) => void;
  textKeys?: {
    allDepots: string;
    amountDepots: string;
    noDepots: string;
  };
  icon?: ReactNode;
  onSave?: () => void;
  onBack?: () => void;
} & TypographyProps;

export const DepotsSelector = ({
  selectedGroup,
  accountParams,
  selectedGroupParams,
  color = "text.primary",
  textKeys = {
    allDepots: "depotsSelector.allPortfolios",
    amountDepots: "depotsSelector.amountPortfolios",
    noDepots: "depotsSelector.noPortfolios",
  },
  icon,
  onSave,
  showOptions,
  onBack,
  onShowClick,
  ...rest
}: Props) => {
  const { accounts } = usePublicOrPrivateAccounts(accountParams);
  const { mutateAsync } = useCreateSelectedGroups();
  const [isDepotSelectorModalOpen, setIsDepotSelectorModalOpen] =
    useState(false);
  const [checkedPortfolios, setCheckedPortfolios] = useState<number[]>(
    selectedGroup?.accountIds || []
  );
  const [prevAccountIds, setPrevAccountIds] = useState(
    selectedGroup?.accountIds
  );
  if (prevAccountIds !== selectedGroup?.accountIds) {
    setPrevAccountIds(selectedGroup?.accountIds);
    setCheckedPortfolios(selectedGroup?.accountIds || []);
  }
  const visibleAccounts = filterOutHiddenAccounts(accounts);
  const isOutsideModal = Boolean(onBack) && Boolean(onShowClick);

  const saveSelectedAccounts = useCallback(async () => {
    const payload = {
      name: selectedGroupParams.id,
      accountIds: checkedPortfolios,
    };
    await mutateAsync(payload);
    onSave?.();
  }, [checkedPortfolios, mutateAsync, onSave, selectedGroupParams.id]);

  return (
    <Box color={color}>
      {(!isOutsideModal || !showOptions) && (
        <DepotsSelectorButton
          accounts={visibleAccounts}
          selectedGroup={selectedGroup}
          accountParams={accountParams}
          selectedGroupParams={selectedGroupParams}
          color={color}
          textKeys={textKeys}
          icon={icon}
          onButtonClick={() => {
            setIsDepotSelectorModalOpen(true);
            onShowClick?.(true);
          }}
          {...rest}
        />
      )}
      {isOutsideModal ? (
        <>
          {showOptions && (
            <DepotsSelectorBody
              // @ts-ignore TODO: backend fix
              accounts={visibleAccounts}
              onConfirm={saveSelectedAccounts}
              checkedPortfolios={checkedPortfolios}
              onSelect={setCheckedPortfolios}
              onClose={onBack}
            />
          )}
        </>
      ) : (
        <DepotsSelectorModal
          // @ts-ignore TODO: backend fix
          accounts={visibleAccounts}
          onConfirm={saveSelectedAccounts}
          isOpen={isDepotSelectorModalOpen}
          checkedPortfolios={checkedPortfolios}
          onSelect={setCheckedPortfolios}
          onClose={() => setIsDepotSelectorModalOpen(false)}
        />
      )}
    </Box>
  );
};
